import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pill, Trash2, Plus, IndianRupee, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useLocation as useGeo } from "@/contexts/LocationContext";
import LocationBar from "@/components/LocationBar";

const CATALOG = [
  { name: "Paracetamol 500mg", price: 25 },
  { name: "Amoxicillin 500mg", price: 60 },
  { name: "Metformin 500mg", price: 45 },
  { name: "Atorvastatin 10mg", price: 90 },
  { name: "Cetirizine 10mg", price: 20 },
  { name: "Pantoprazole 40mg", price: 75 },
];

export default function Pharmacy() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { loc } = useGeo();
  const [pharmas, setPharmas] = useState([]);
  const [selected, setSelected] = useState(null);
  const [cart, setCart] = useState([]);
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const p = loc ? { near_lat: loc.lat, near_lng: loc.lng } : {};
    api.get("/pharmacies", { params: p }).then((r) => setPharmas(r.data));
  }, [loc?.lat, loc?.lng]);

  const addToCart = (m) => {
    setCart((c) => {
      const ex = c.find(x => x.name === m.name);
      if (ex) return c.map(x => x.name === m.name ? { ...x, qty: x.qty + 1 } : x);
      return [...c, { ...m, qty: 1 }];
    });
  };
  const total = cart.reduce((s, m) => s + m.price * m.qty, 0);

  const placeOrder = async () => {
    if (!user) { nav("/login"); return; }
    if (!cart.length || !address) { toast.error("Add medicines & address"); return; }
    setLoading(true);
    try {
      await api.post("/orders", {
        pharmacy_id: selected.id, medicines: cart, address,
      });
      toast.success("Order placed — delivery in ~45 mins");
      setSelected(null); setCart([]);
      nav("/patient");
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-teal-700">Pharmacy</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Order medicines home delivered</h1>
          <p className="text-slate-600 text-sm mt-1">Choose a nearby verified pharmacy and get delivery in under an hour.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/ai-triage"><Button variant="outline" data-testid="pharm-ai-cta"><Sparkles className="w-4 h-4 mr-2" />Ask MedHOne AI</Button></Link>
          <LocationBar />
        </div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {pharmas.map((p) => (
          <Card key={p.id} className="p-5 rounded-2xl card-lift" data-testid={`pharmacy-card-${p.id}`}>
            <div className="w-11 h-11 rounded-xl bg-amber-500/15 flex items-center justify-center text-amber-700">
              <Pill className="w-5 h-5" />
            </div>
            <Link to={`/facility/pharmacy/${p.id}`} className="font-display font-bold mt-3 block hover:text-teal-700 transition-colors" data-testid={`pharmacy-detail-${p.id}`}>{p.name}</Link>
            <div className="text-xs text-slate-500">{p.address}</div>
            <div className="mt-2 flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]">~{p.delivery_time_min} min delivery</Badge>
              {p.distance_km != null && <Badge variant="outline" className="text-[10px]">{p.distance_km} km</Badge>}
            </div>
            <div className="mt-4 flex justify-end">
              <Button data-testid={`order-btn-${p.id}`} size="sm" className="bg-amber-600 hover:bg-amber-500 text-white" onClick={() => setSelected(p)}>Order</Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Order from {selected?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Medicines</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {CATALOG.map((m) => (
                  <Button key={m.name} variant="outline" size="sm" onClick={() => addToCart(m)} data-testid={`add-${m.name.replace(/\s+/g, '-')}`} className="justify-between">
                    <span className="text-xs">{m.name}</span>
                    <span className="text-xs font-mono">₹{m.price}</span>
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Your cart</Label>
              {cart.length === 0 && <div className="text-sm text-slate-500 mt-1">Empty</div>}
              {cart.map((m, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b">
                  <div className="text-sm">{m.name} <span className="text-slate-500">×{m.qty}</span></div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">₹{m.price * m.qty}</span>
                    <Button variant="ghost" size="icon" onClick={() => setCart(cart.filter((_, j) => j !== i))}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              <div className="mt-3 flex items-center justify-between font-semibold">
                <span>Total</span>
                <span className="font-mono inline-flex items-center"><IndianRupee className="w-4 h-4" />{total}</span>
              </div>
            </div>
            <div>
              <Label>Delivery address</Label>
              <Input data-testid="delivery-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Flat, street, area" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="place-order-btn" disabled={loading} onClick={placeOrder} className="bg-amber-600 hover:bg-amber-500 text-white">
              {loading ? "Placing…" : `Place order • ₹${total}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
